import { Phone, Mail, Clock, MessageSquare } from 'lucide-react';

export default function ContactPage() {
  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Get In Touch</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Have questions? Want to discuss your situation? We're here to help. Reach out today.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h2>

            <div className="space-y-8 mb-12">
              <div className="flex gap-4">
                <Phone className="w-8 h-8 text-blue-900 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <a href="tel:412-284-6681" className="text-blue-900 hover:text-blue-800 text-lg font-semibold">
                    412-284-6681
                  </a>
                  <p className="text-gray-600 text-sm mt-1">Call for immediate assistance</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Mail className="w-8 h-8 text-blue-900 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <a href="mailto:quickclosehomeoffers@gmail.com" className="text-blue-900 hover:text-blue-800 text-lg font-semibold">
                    quickclosehomeoffers@gmail.com
                  </a>
                  <p className="text-gray-600 text-sm mt-1">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex gap-4">
                <Clock className="w-8 h-8 text-blue-900 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Hours</h3>
                  <p className="text-gray-700 text-lg font-semibold">
                    24/7
                  </p>
                  <p className="text-gray-600 text-sm mt-1">Available whenever you need us</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Fast Turnaround</h3>
              <p className="text-gray-600 text-sm">
                We understand that your time is valuable. That\'s why we prioritize quick responses and efficient processes. You won\'t be waiting weeks to hear from us.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 p-8 rounded-xl border-2 border-blue-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Us Directly</h3>
                <p className="text-gray-600">
                  Choose your preferred method - we'll get back to you within 24 hours
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <a
                  href="mailto:quickclosehomeoffers@gmail.com"
                  className="flex flex-col items-center gap-3 bg-white p-6 rounded-lg hover:shadow-lg transition-all border-2 border-blue-200 hover:border-blue-900 group"
                >
                  <Mail className="w-12 h-12 text-blue-900" />
                  <div className="text-center">
                    <div className="font-bold text-gray-900 mb-1">Email</div>
                    <div className="text-sm text-blue-900 font-semibold break-all">quickclosehomeoffers@gmail.com</div>
                  </div>
                </a>

                <a
                  href="sms:412-284-6681"
                  className="flex flex-col items-center gap-3 bg-white p-6 rounded-lg hover:shadow-lg transition-all border-2 border-blue-200 hover:border-blue-900 group"
                >
                  <MessageSquare className="w-12 h-12 text-blue-900" />
                  <div className="text-center">
                    <div className="font-bold text-gray-900 mb-1">Text</div>
                    <div className="text-sm text-blue-900 font-semibold">412-284-6681</div>
                  </div>
                </a>
              </div>

              <div className="bg-white p-6 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-gray-900 mb-3">What to include in your message:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 font-bold">•</span>
                    <span>Your name and contact information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 font-bold">•</span>
                    <span>Property address you're looking to sell</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 font-bold">•</span>
                    <span>Brief description of your situation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-900 font-bold">•</span>
                    <span>Any timeline or urgency considerations</span>
                  </li>
                </ul>
              </div>

              <p className="text-xs text-gray-500 text-center mt-6">
                Your information is kept completely private and secure.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Other Ways to Connect</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Call</h3>
              <p className="text-gray-600 mb-6">
                Have a quick question? Call us. We're available 24/7 for your convenience.
              </p>
              <a
                href="tel:412-284-6681"
                className="inline-block bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold text-sm"
              >
                Call Now
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Email Us</h3>
              <p className="text-gray-600 mb-6">
                Prefer email? Send us your details and we\'ll respond within 24 hours with information or next steps.
              </p>
              <a
                href="mailto:quickclosehomeoffers@gmail.com"
                className="inline-block bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold text-sm"
              >
                Send Email
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Get an Offer</h3>
              <p className="text-gray-600 mb-6">
                The fastest way to get started. Submit your property and get a cash offer within 24-48 hours.
              </p>
              <a
                href="https://www.quickclosehomeoffer.com/home/"
                className="inline-block bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold text-sm"
              >
                Start Here
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-950">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Questions About the Process?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Check out our FAQ section or reach out directly. We're here to answer everything.
          </p>
          <a
            href="/faq"
            className="inline-block bg-white text-blue-900 px-8 py-3 rounded-lg hover:shadow-lg transition-all font-semibold"
          >
            View FAQ
          </a>
        </div>
      </section>
    </div>
  );
}
